import { jsx, jsxs } from 'react/jsx-runtime';
import { Breadcrumbs } from './Breadcrumbs.js';
import { DetailPageHeader } from './DetailPageHeader.js';
import { DetailPageHero } from './DetailPageHero.js';
import { PageContainer } from './PageContainer.js';
import { useApiClient } from '../../lib/api-client.js';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';

/**
 * Generic detail page shell for tour/attraction/hotel-style entities.
 * Owns the fetch, not-found fallback, breadcrumbs + header + hero chrome, and
 * base-path → first-tab redirect. Child routes receive the entity via
 * `useOutletContext` under the configured `contextKey`.
 */
function EntityDetailShell({ resource, queryKey, id, basePath, entityLabel, tabs, hero, contextKey, breadcrumbs, headerActions, children, }) {
    const apiClient = useApiClient();
    const navigate = useNavigate();
    const location = useLocation();
    const { data, isLoading } = useQuery({
        queryKey: [queryKey, id],
        queryFn: () => apiClient.get(`/${resource}/${id}`),
        enabled: !!id,
    });
    const entity = data?.data;
    useEffect(() => {
        if (!id || tabs.length === 0)
            return;
        const firstTabPath = tabs[0].path;
        if (location.pathname === basePath || location.pathname === `${basePath}/`) {
            navigate(`${basePath}/${firstTabPath}`, { replace: true });
        }
    }, [id, basePath, location.pathname, navigate, tabs]);
    if (!id) {
        return (jsx(PageContainer, { children: jsxs("div", { className: "text-center", children: [jsxs("h2", { className: "text-lg font-medium mb-2", children: [entityLabel, " Not Found"] }), jsxs("p", { className: "text-muted-foreground", children: ["The requested ", entityLabel.toLowerCase(), " could not be found."] })] }) }));
    }
    const title = hero.title(entity);
    const imageUrl = hero.imageUrl ? hero.imageUrl(entity) : null;
    const videoUrl = hero.videoUrl ? hero.videoUrl(entity) : null;
    const imageAlt = hero.imageAlt ? hero.imageAlt(entity) : title;
    return (jsxs(PageContainer, { padding: "none", className: "flex flex-col overflow-hidden", children: [jsx(DetailPageHeader, { actions: headerActions ? headerActions(entity) : undefined, children: jsx(Breadcrumbs, { items: breadcrumbs(entity) }) }), jsx(DetailPageHero, { isLoading: isLoading, category: hero.category, title: title, image: { url: imageUrl, alt: imageAlt, fallbackIcon: hero.fallbackIcon, videoUrl }, meta: entity && hero.meta ? hero.meta(entity) : undefined, badges: entity && hero.badges ? hero.badges(entity) : undefined, tabs: [...tabs], basePath: basePath, children: jsx(Outlet, { context: { [contextKey]: entity, isLoading } }) }), children] }));
}

export { EntityDetailShell };
//# sourceMappingURL=EntityDetailShell.js.map
